function convertAwsDateToIso(date: string): Date {
  const year = date.substring(0, 4);
  const month = date.substring(4, 6);
  const day = date.substring(6, 8);
  const hour = date.substring(9, 11);
  const minute = date.substring(11, 13);
  const isoDateStr = `${year}-${month}-${day}T${hour}:${minute}:00Z`;
  const isoDate = new Date(isoDateStr);
  if (isNaN(isoDate.getTime())) {
    throw new Error('Invalid ISO date');
  }
  return isoDate;
}

/**
 * Checks if the given URL is expired based on its query parameters.
 * Supports two formats:
 * 1. Standard S3 URLs with 'X-Amz-Date' and 'X-Amz-Expires' query parameters
 * 2. Custom URLs with an 'exp' parameter containing a Unix timestamp
 *
 * Will return false if the URL is malformed or does not contain the
 * required expiration parameters.
 *
 * @param {string} url - The URL to check.
 * @returns {boolean} - Returns true if the URL is expired, otherwise false.
 */
export function isS3UrlExpired(url: string) {
  let urlObj;
  try {
    urlObj = new URL(url);
  } catch (e) {
    return false;
  }

  const currentDate = new Date();
  let expiresDate: Date | undefined;

  // Check for custom format with direct expiration timestamp
  const expTimestamp = urlObj.searchParams.get('exp');
  if (expTimestamp) {
    const expiresTimestamp = parseInt(expTimestamp, 10);
    if (isNaN(expiresTimestamp)) {
      return false;
    }
    expiresDate = new Date(expiresTimestamp * 1000); // Convert from seconds to milliseconds
  } else {
    // Fall back to standard S3 URL format
    const date = urlObj.searchParams.get('X-Amz-Date');
    const expires = urlObj.searchParams.get('X-Amz-Expires');
    if (!date || !expires) {
      return false;
    }

    const requestDate = convertAwsDateToIso(date);
    const expiresInt = parseInt(expires, 10);
    if (isNaN(expiresInt)) {
      return false;
    }
    expiresDate = new Date(requestDate.getTime() + expiresInt * 1000);
  }
  if (!expiresDate) {
    return false;
  }
  return expiresDate < currentDate;
}
